import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FloppyDisk, Sparkle, Trash } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/format";

const slug = (s) => String(s).replace(/\s+/g, "-");

export default function RateBandSettings() {
  const [groups, setGroups] = useState([]);
  const [bands, setBands] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/settings/pricing"),
      api.get("/settings/rate-bands").catch(() => ({ data: {} })),
    ])
      .then(([p, rb]) => {
        const gmap = p.data?.pricing?.groups || p.data?.groups || {};
        const list = Object.entries(gmap)
          .map(([name, rate]) => ({ name, rate: Number(rate) || 0 }))
          .sort(
            (a, b) =>
              (Number(a.name.replace(/\D/g, "")) || 0) -
              (Number(b.name.replace(/\D/g, "")) || 0),
          );
        setGroups(list);
        setBands(rb.data && typeof rb.data === "object" ? rb.data : {});
      })
      .catch(() => {});
  }, []);

  const setField = (name, key, value) =>
    setBands((b) => ({ ...b, [name]: { ...(b[name] || {}), [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [name, v] of Object.entries(bands)) {
        const entry = {};
        if (v?.low !== "" && v?.low != null) entry.low = Number(v.low);
        if (v?.high !== "" && v?.high != null) entry.high = Number(v.high);
        if (Object.keys(entry).length) payload[name] = entry;
      }
      const { data } = await api.put("/settings/rate-bands", { bands: payload });
      setBands(data && typeof data === "object" ? data : {});
      toast.success("Rate bands saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    } finally {
      setSaving(false);
    }
  };

  const suggest = () => {
    setBands((b) => {
      const next = { ...b };
      for (const g of groups) {
        if (!g.rate) continue;
        const cur = next[g.name] || {};
        next[g.name] = {
          low: cur.low != null && cur.low !== "" ? cur.low : Math.round(g.rate),
          high:
            cur.high != null && cur.high !== ""
              ? cur.high
              : Math.round(g.rate * 3.5),
        };
      }
      return next;
    });
    toast.message("Suggested bands filled the blanks — review, then Save");
  };

  const clearAll = () => {
    setBands({});
    toast.message("All bands cleared to auto — Save to apply");
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Material Rate Bands" />
      <main className="relative z-10 mx-auto max-w-2xl px-4 pb-20 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">
          Settings
        </div>
        <h1
          className="text-2xl font-bold tracking-tight sm:text-3xl"
          data-testid="rate-bands-title"
        >
          Material Rate Bands
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          Set the expected installed price per ft² for each material group. When a
          drawing's live price per ft² falls outside its band, the design studio flags
          it in amber so staff catch a mispriced job before nesting. Leave a field blank
          to use the automatic band (base rate up to 4× base).
        </p>
        <section className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
          <div className="grid grid-cols-[1fr_6rem_6rem] items-center gap-x-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            <span>Material group</span>
            <span className="text-center">Low $/ft²</span>
            <span className="text-center">High $/ft²</span>
          </div>
          <div className="mt-2 space-y-2">
            {groups.map((g) => (
              <div
                key={g.name}
                className="grid grid-cols-[1fr_6rem_6rem] items-center gap-x-3"
                data-testid={`rate-band-row-${slug(g.name)}`}
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-zinc-100">
                    {g.name}
                  </div>
                  <div className="font-mono-data text-[11px] text-zinc-500">
                    base {money(g.rate)}/ft²
                  </div>
                </div>
                <Input
                  data-testid={`rate-band-low-${slug(g.name)}`}
                  type="number"
                  min="0"
                  step="1"
                  value={bands[g.name]?.low ?? ""}
                  onChange={(e) => setField(g.name, "low", e.target.value)}
                  placeholder="auto"
                  className="h-10 w-24 border-zinc-800 bg-zinc-950 text-center"
                />
                <Input
                  data-testid={`rate-band-high-${slug(g.name)}`}
                  type="number"
                  min="0"
                  step="1"
                  value={bands[g.name]?.high ?? ""}
                  onChange={(e) => setField(g.name, "high", e.target.value)}
                  placeholder="auto"
                  className="h-10 w-24 border-zinc-800 bg-zinc-950 text-center"
                />
              </div>
            ))}
            {groups.length === 0 && (
              <p className="rounded-md border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-500">
                No material groups found in pricing.
              </p>
            )}
          </div>
        </section>
        <div className="mt-4 flex gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="rate-bands-suggest"
            onClick={suggest}
            className="border-zinc-700 bg-transparent"
          >
            <Sparkle size={14} weight="bold" className="mr-1" /> Suggest bands
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="rate-bands-clear"
            onClick={clearAll}
            className="border-zinc-700 bg-transparent text-zinc-400 hover:text-zinc-100"
          >
            <Trash size={14} weight="bold" className="mr-1" /> Clear all
          </Button>
          <Button
            type="button"
            data-testid="rate-bands-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" />{" "}
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </main>
    </div>
  );
}
